import 'react-day-picker/dist/style.css'

import { useMask } from '@react-input/mask'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { getDisabledDates, getMonths, getYearOptions } from '@repo/toolkit/date'
import { AvailableLanguages } from '@repo/toolkit/language-types'
import { YearOptionsType } from '@repo/toolkit/types'
import { useBreakPoint } from '@repo/toolkit/use-break-point'
import { format, getYear, isAfter, isBefore, isValid, parse, parseISO } from 'date-fns'
import { useEffect, useRef, useState } from 'react'
import { DayPicker } from 'react-day-picker'
import { useOnClickOutside } from 'usehooks-ts'

import useTranslate from '../../../../apps/backoffice/src/lib/hooks/use-translate'
import { CalendarOneIcon } from '../../icons'
import { calendarFormatters } from './date-range-input/utils'
import InputWrapper, { InputWrapperProps } from './input/input-wrapper'
import SelectInput from './select-input'
import { typography } from './typography'

const translations = {
  en: { month: 'Month', year: 'Year', formatHint: 'DD/MM/YYYY' },
  fr: { month: 'Mois', year: 'Année', formatHint: 'JJ/MM/AAAA' }
}

export type DateStringInputProps = {
  language?: AvailableLanguages
  value?: string | null
  iconIsRight?: boolean
  readOnly?: boolean
  mode?: 'over' | 'under' | 'dynamic' // dynamic (on desktop in the middle and over on mobile)
  disabledPastDates?: boolean
  disabledFutureDates?: boolean
  allowedDates?: Date[]
  // days of the week to disable (0 = Sunday ... 6 = Saturday)
  disabledDaysOfWeek?: number[]
  type?: YearOptionsType
  required?: boolean
  startDate?: Date
  endDate?: Date
  onChange?: (date: string | undefined | null) => void
} & Omit<
  InputWrapperProps,
  | 'children'
  | 'className'
  | 'icon'
  | 'iconStyle'
  | 'iconToRight'
  | 'active'
  | 'iconToTheRight'
  | 'focused'
>

const DateStringInput: React.FC<DateStringInputProps> = ({
  language = 'fr',
  value,
  iconIsRight = true,
  mode = 'under',
  type = 'default',
  startDate,
  endDate,
  allowedDates,
  disabledPastDates,
  disabledFutureDates,
  disabledDaysOfWeek,
  onChange,
  ...props
}) => {
  const device = useBreakPoint()
  const { currentLanguage, getLocale } = useTranslate()
  const locale = getLocale()
  const ref = useRef<HTMLDivElement>(null)
  const inputRef = useMask({ mask: '__/__/____', replacement: { _: /\d/ } })
  const isMobile = device === 'mobile'

  const parsedInitialDate = value ? new Date(value) : undefined
  const [selected, setSelected] = useState<Date | undefined>(
    parsedInitialDate && isValid(parsedInitialDate) ? parsedInitialDate : undefined
  )
  const [inputValue, setInputValue] = useState(() => {
    const parsed = value ? parseISO(value) : undefined
    return parsed && isValid(parsed) ? format(parsed, 'dd/MM/yyyy') : ''
  })

  const [currentMonth, setCurrentMonth] = useState<Date>(selected ?? startDate ?? new Date())
  const [isOpen, setIsOpen] = useState(false)

  useOnClickOutside(ref, () => setIsOpen(false))

  const handleYearChangeByOffset = (offset: number) => {
    setCurrentMonth((prev) => {
      const newDate = new Date(prev)
      newDate.setFullYear(prev.getFullYear() + offset)
      return newDate
    })
  }

  const handleDateChange = (date?: Date) => {
    if (!date || !isValid(date)) {
      setSelected(undefined)
      setInputValue('')
      onChange?.(null)
      return
    }

    const formatted = format(date, 'dd/MM/yyyy')
    const dateString = format(date, 'yyyy-MM-dd')

    setSelected(date)
    setInputValue(formatted)
    onChange?.(dateString)
    handleYearChangeByOffset(date.getFullYear() - currentMonth.getFullYear())
  }

  const handleChange = (date: Date) => {
    setCurrentMonth(date)
    setIsOpen(false)
    handleDateChange(date)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    setInputValue(val)

    if (val === '') return handleDateChange(undefined)

    if (val.length === 10) {
      const parsedDate = parse(val, 'dd/MM/yyyy', new Date())

      const year = getYear(parsedDate)
      const years = getYearOptions(type)

      const today = new Date()
      today.setHours(0, 0, 0, 0) // Start of today

      const isInRange =
        years.includes(year) &&
        (!disabledPastDates || !isBefore(parsedDate, today)) &&
        (!disabledFutureDates || !isAfter(parsedDate, today)) &&
        !disabledDaysOfWeek?.includes(parsedDate.getDay())

      if (isInRange) {
        handleDateChange(parsedDate)
      } else {
        setInputValue('')
        handleDateChange(undefined)
        inputRef.current?.blur()
      }
    }
  }

  useEffect(() => {
    const parsed = value ? parseISO(value) : undefined
    if (parsed && isValid(parsed)) {
      setSelected(parsed)
      setCurrentMonth(parsed)
      setInputValue(format(parsed, 'dd/MM/yyyy'))
    } else {
      setSelected(undefined)
      setInputValue('')
    }
  }, [value])

  useEffect(() => {
    // When startDate changes and there's no selected value, update currentMonth to startDate
    if (startDate && !selected) {
      setCurrentMonth(startDate)
    }
  }, [startDate, selected])

  const months = getMonths(currentLanguage)
  const years = getYearOptions(type)
  const disabledDates = getDisabledDates({ disabledPastDates, disabledFutureDates })

  return (
    <div
      ref={ref}
      className={cn('relative w-full', {
        'pointer-events-none cursor-not-allowed': props.disabled || props.readOnly
      })}
    >
      <InputWrapper
        {...props}
        active={isOpen || !!selected}
        focused={isOpen}
        icon={CalendarOneIcon}
        // Toggle the calendar when the icon is clicked (open -> close, close -> open)
        iconOnClick={
          !props.disabled && !props.readOnly ? () => setIsOpen((prev) => !prev) : undefined
        }
        iconToTheRight={iconIsRight}
        className="cursor-pointer"
        onClick={!props.disabled && !props.readOnly ? () => inputRef.current?.focus() : undefined}
        required={props.required}
      >
        <input
          ref={inputRef}
          type={isMobile ? 'tel' : 'text'}
          value={inputValue}
          onChange={handleInputChange}
          style={typography.label}
          placeholder={translations[language].formatHint}
          className={cn('w-full bg-transparent outline-none h-9', {
            'text-grey opacity-20': props.disabled,
            'text-black': !props.disabled
          })}
          disabled={props.disabled || props.readOnly}
          readOnly={isMobile}
        />
      </InputWrapper>

      {isOpen && (
        <div
          className={cn(
            'bg-white absolute z-20 w-fit py-5  rounded drop-shadow-2xl mt-2 text-xs transition-all duration-150 ease-out transform origin-top -left-2',
            {
              'fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2':
                mode === 'over' || (isMobile && mode === 'dynamic'),
              '-translate-y-1/2 -translate-x-1/2 left-1/2': !isMobile && mode === 'dynamic'
            }
          )}
        >
          <div className="flex mb-2 px-2 justify-between">
            <SelectInput
              label={translations[language].month}
              options={months.map((label, index) => ({ value: index, label }))}
              selectedOption={{
                value: currentMonth.getMonth(),
                label: months[currentMonth.getMonth()]
              }}
              onChange={(val) => setCurrentMonth(new Date(currentMonth.setMonth(val)))}
              className="bg-white rounded"
            />

            <SelectInput
              label={translations[language].year}
              options={years.map((year) => ({ value: year, label: year.toString() }))}
              selectedOption={{
                value: currentMonth.getFullYear(),
                label: currentMonth.getFullYear().toString()
              }}
              onChange={(val) => handleYearChangeByOffset(val - currentMonth.getFullYear())}
              className="bg-white rounded"
            />
          </div>
          <DayPicker
            month={currentMonth}
            selected={selected}
            fromDate={startDate}
            toDate={endDate}
            onDayClick={handleChange}
            onMonthChange={setCurrentMonth}
            disabled={[
              ...(disabledDaysOfWeek?.length ? [{ dayOfWeek: disabledDaysOfWeek }] : []),
              ...(allowedDates
                ? [
                    (date: Date) =>
                      !allowedDates.some(
                        (allowed) => allowed.toDateString() === date.toDateString()
                      )
                  ]
                : disabledDates
                  ? [disabledDates]
                  : [])
            ]}
            onSelect={setSelected}
            modifiers={{
              start: startDate ? [startDate] : []
            }}
            modifiersClassNames={{
              start: '!font-bold'
            }}
            classNames={{
              day: 'rounded-sm h-8 w-8 hover:!bg-primary-light transition-colors',
              day_selected: '!bg-primary-light !text-primary'
            }}
            locale={locale}
            formatters={calendarFormatters(locale)}
            onDayBlur={() => setIsOpen(false)}
          />
        </div>
      )}
    </div>
  )
}

export default DateStringInput
